import { useApi } from '@hero/context';

import type {
  UseInvoiceApi,
  UseAllUnpaidInvoicesApi,
  UseUnpaidTotalApi,
  UseCalcMultiInvoicesApi,
  UseInvoiceListApi,
  UseInvoiceOrdersApi,
  UseInvoiceCarrierUploadInvoiceProofApi,
  UseEligibleLegsApi,
  UseCaptureLegsApi,
  UsePayInvoiceApi,
} from './types';
import { useInfiniteQueryAPI, useMutationAPI, useQueryAPI } from '../../utils';

/**
 * Get a single invoice by ID.
 *
 * Hook API: {@link UseInvoiceApi}
 */
export const useInvoiceApi: UseInvoiceApi = (id, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'invoice',
    apiFn: api.payment.getInvoice,
    query: undefined,
    singleIdQuery: id,
    apiConfig,
    isEnabled: () => !!id,
  });
};

/**
 * List current user invoices with pagination and optional search/status filter.
 * API from ApiProvider (useApi()); works in any app without overrides.
 *
 * Hook API: {@link UseInvoiceListApi}
 */
export const useInvoiceListApi: UseInvoiceListApi = (query, apiConfig) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'invoice',
    apiFn: api.payment.getCurrentUserInvoices,
    query,
    apiConfig,
  });
};

/**
 * List orders for a specific invoice with pagination.
 * API from ApiProvider (useApi()); works in any app without overrides.
 *
 * Hook API: {@link UseInvoiceOrdersApi}
 */
export const useInvoiceOrdersApi: UseInvoiceOrdersApi = (query, apiConfig) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'invoice',
    apiFn: api.payment.getInvoiceOrders,
    query,
    apiConfig,
  });
};

/**
 * Calculate totals for selected invoices (amounts, breakdown, cashback).
 *
 * **Customer-only** — uses `useApi('customer')`. Only call from components
 * that render exclusively in customer context.
 *
 * Hook API: {@link UseCalcMultiInvoicesApi}
 */
export const useCalcMultiInvoicesApi: UseCalcMultiInvoicesApi = (query, apiConfig) => {
  const api = useApi('customer');
  return useQueryAPI({
    entity: 'invoice',
    apiFn: api.payment.calcMultiInvoices,
    query,
    apiConfig,
  });
};

/**
 * Fetch all unpaid/partially_paid invoices for the current user.
 * Sorted by due date ascending (oldest first). No pagination.
 *
 * **Customer-only** — uses `useApi('customer')`. Only call from components
 * that render exclusively in customer context.
 *
 * Hook API: {@link UseAllUnpaidInvoicesApi}
 */
export const useAllUnpaidInvoicesApi: UseAllUnpaidInvoicesApi = apiConfig => {
  const api = useApi('customer');
  return useQueryAPI({
    entity: 'invoice',
    apiFn: api.payment.getAllUnpaidInvoices,
    query: undefined,
    apiConfig,
  });
};

/**
 * Get aggregated total (amount + count) of all unpaid invoices for the current user.
 *
 * **Customer-only** — uses `useApi('customer')`. Only call from components
 * that render exclusively in customer context.
 *
 * Hook API: {@link UseUnpaidTotalApi}
 */
export const useUnpaidTotalApi: UseUnpaidTotalApi = apiConfig => {
  const api = useApi('customer');
  return useQueryAPI({
    entity: 'unpaidTotal',
    apiFn: api.payment.getUnpaidTotal,
    query: undefined,
    apiConfig,
  });
};

/**
 * Upload invoice proof as a carrier.
 * API from ApiProvider (useApi('carrier')); carrier-only endpoint.
 *
 * Hook API: {@link UseInvoiceCarrierUploadInvoiceProofApi}
 */
export const useInvoiceCarrierUploadInvoiceProofApi: UseInvoiceCarrierUploadInvoiceProofApi =
  apiConfig => {
    const api = useApi('carrier');
    return useMutationAPI({
      entity: 'invoice',
      apiFn: api.payment.carrierUploadInvoiceProof,
      apiConfig,
    });
  };

/**
 * List delivered legs the viewer can turn into invoices. Seller-
 * scoped server-side — the viewer's org is implicitly pinned as
 * the seller, so no `asRole` knob is exposed on the hook surface.
 *
 * Lives on its own `eligibleLegs` entity so order-side mutations
 * (price edits from the `OrderDrawer`, contract status changes,
 * etc.) also bust it via the cross-bust entries on the `order`
 * and `invoice` entity configs. That keeps the picker honest
 * whenever the user opens the linked OrderDrawer, edits the
 * price, and comes back — the leg total in the picker matches
 * what the backend would actually invoice on submit.
 *
 * Hook API: {@link UseEligibleLegsApi}
 */
export const useEligibleLegsApi: UseEligibleLegsApi = (query, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'eligibleLegs',
    apiFn: api.payment.getEligibleLegs,
    query,
    apiConfig,
  });
};

/**
 * Bundle the selected legs into ONE invoice (one `items[]` row
 * per leg) and create it. Invalidates `invoice` (the list + the
 * picker via the cross-bust on `eligibleLegs`) and
 * `accountTransaction` (the merge path posts one
 * `leg_capture` AccountTransaction for the batch).
 *
 * Hook API: {@link UseCaptureLegsApi}
 */
export const useCaptureLegsApi: UseCaptureLegsApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'invoice',
    apiFn: api.payment.captureLegs,
    apiConfig,
  });
};

/**
 * Record an off-platform payment against a single invoice. Any
 * party on `Invoice.partyIds` may call it; the backend caps the
 * amount to `Invoice.amount.remaining` and creates a matching
 * `AccountTransaction` (so we bust the `accountTransaction`
 * entity via the cross-bust on `invoice`).
 *
 * Hook API: {@link UsePayInvoiceApi}
 */
export const usePayInvoiceApi: UsePayInvoiceApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'invoice',
    apiFn: api.payment.payInvoice,
    apiConfig,
  });
};
